import React, { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Button
} from "@mui/material";
import { useAuth } from "../../context/AuthContext";
import useFetchObjects from "../../api/useFetchObjects";
import { formatJalaliDate } from "../utils/dateFormat";

export default function SalesDataTable() {
  const auth = useAuth();
  const token = auth?.user?.token;
  const [selectedCustomer, setSelectedCustomer] = useState("");

  const queryString = useMemo(() => {
    const params = ["page_size=all"];
    if (selectedCustomer) params.push(`customer=${selectedCustomer}`);
    return `?${params.join("&")}`;
  }, [selectedCustomer]);

  const { data: salesData, isLoading } = useFetchObjects(
    ["all-sales", queryString],
    `shop/sales/${queryString}`,
    token
  );

  const { data: customersData } = useFetchObjects(
    ["customers"],
    "shop/customers?page_size=all",
    token
  );

  const sales = salesData?.results || salesData || [];
  const customers = customersData?.results || customersData || [];

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography>Loading sales data...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Sales Data Table
      </Typography>
      
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Customer</InputLabel>
              <Select
                value={selectedCustomer}
                onChange={(e) => setSelectedCustomer(e.target.value)}
                label="Customer"
              >
                <MenuItem value="">All Customers</MenuItem>
                {customers.map((customer) => (
                  <MenuItem key={customer.id} value={customer.id}>
                    {customer.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button variant="outlined" onClick={() => setSelectedCustomer("")} fullWidth>
              Clear Filter
            </Button>
          </Grid>
        </Grid>
      </Paper>
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Branch</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Sale Date</TableCell>
              <TableCell>Discount</TableCell>
              <TableCell>Total Amount</TableCell>
              <TableCell>Total Paid</TableCell>
              <TableCell>Total Remained</TableCell>
              <TableCell>Item Count</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sales.map((sale) => (
              <TableRow key={sale.id}>
                <TableCell>{sale.id}</TableCell>
                <TableCell>{sale.shop_details?.name || "-"}</TableCell>
                <TableCell>{sale.customer_details?.name || "-"}</TableCell>
                <TableCell>{formatJalaliDate(sale.sale_date)}</TableCell>
                <TableCell>{sale.discount}</TableCell>
                <TableCell>{sale.total_amount}</TableCell>
                <TableCell>{sale.total_paid}</TableCell>
                <TableCell>{sale.total_remained_amount}</TableCell>
                <TableCell>{sale.sale_items?.length || 0}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      <Typography variant="body2" sx={{ mt: 2 }}>
        Total Records: {sales.length}
      </Typography>
    </Box>
  );
}
